import { useCallback } from 'react'
import api from '../../lib/commons/api'
import { getErrorCode } from '../../infrastructure/api/error'
import {
  ApplyStructureChangesParams,
  ReorderPageRowsParams,
  UpdatePageConfigParams,
  UpdatePageContentParams,
  UpdatePageRowParams,
  UpdateVersionNameParams,
  VersionReportVersionRepository,
} from '../../applications/ports/versionReportVersionRepository'
import {
  EvaluateOverviewRequest,
  EvaluateProgressReportByProcessRequest,
  EvaluateProgressReportByTeamRequest,
} from '../../domain/entity/version-report/evaluation'
import { dateTimeV2Service } from '../../domain/value-object/DateTimeV2'

export const useVersionReportVersionRepository =
  (): VersionReportVersionRepository => {
    const fetchVersions = useCallback(async (templateUuid: string) => {
      const response = await api.functional.request(
        'GET',
        '/api/v1/projects/version-report/version/list',
        { templateUuid }
      )
      return (response.json || []).map(version => ({
        ...version,
        createdBy: version.createdBy || { uuid: '', name: '' },
        updatedBy: version.updatedBy || { uuid: '', name: '' },
      }))
    }, [])
    const fetch = useCallback(async (uuid: string) => {
      try {
        const response = await api.functional.request(
          'GET',
          '/api/v1/projects/version-report/version',
          { uuid }
        )
        if (!response.json) {
          return undefined
        }
        return {
          uuid: response.json.uuid,
          lockVersion: response.json.lockVersion,
          snapshotUuid: response.json.snapshotUuid,
          templateName: response.json.templateName,
          name: response.json.name,
          pages: response.json.pages.map(page => {
            const rows = (page.rows || []).map(row => ({
              uuid: row.uuid,
              lockVersion: row.lockVersion,
              targetUuid: row.targetUuid,
              displayOrder: row.displayOrder,
              status: row.status ?? 'SUNNY',
              comment: row.comment ?? '',
            }))
            const tableRows = rows
              .slice()
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map(r => r.targetUuid)
            return {
              uuid: page.uuid,
              lockVersion: page.lockVersion,
              templatePageUuid: page.templatePageUuid,
              pageTitle: page.pageTitle,
              leadSentence: page.leadSentence,
              pageNumber: page.pageNumber,
              pageType: page.pageType,
              config: {
                ...JSON.parse(page.configJsonString || '{}'),
                type: page.pageType,
                tableRows,
              },
              rows,
            }
          }),
          createdAt: dateTimeV2Service.newFromString(response.json.createdAt),
          createdBy: response.json.createdBy || {
            uuid: '',
            name: '',
          },
          updatedAt: dateTimeV2Service.newFromString(response.json.updatedAt),
          updatedBy: response.json.updatedBy || {
            uuid: '',
            name: '',
          },
          takenAt: dateTimeV2Service.newFromString(response.json.takenAt),
        }
      } catch (e) {
        if (getErrorCode(e) === 'NOT_FOUND') {
          return undefined
        }
        throw e
      }
    }, [])
    const createVersion = useCallback(
      async (templateUuid: string, name: string) => {
        const response = await api.functional.request(
          'POST',
          '/api/v1/projects/version-report/version',
          { templateUuid, name }
        )
        return response.json
      },
      []
    )
    const deleteVersion = useCallback(async (uuid: string) => {
      await api.functional.request(
        'DELETE',
        '/api/v1/projects/version-report/version',
        { uuid }
      )
    }, [])
    const updateVersionName = useCallback(
      async (params: UpdateVersionNameParams) => {
        const response = await api.functional.request(
          'PUT',
          '/api/v1/projects/version-report/version/name',
          params
        )
        return response.json
      },
      []
    )
    const updatePageContent = useCallback(
      async (params: UpdatePageContentParams) => {
        const response = await api.functional.request(
          'PUT',
          '/api/v1/projects/version-report/version/page/content',
          params
        )
        return response.json
      },
      []
    )
    const updatePageConfig = useCallback(
      async (params: UpdatePageConfigParams) => {
        const response = await api.functional.request(
          'PUT',
          '/api/v1/projects/version-report/version/page/config',
          params
        )
        return response.json
      },
      []
    )
    const updatePageRow = useCallback(async (params: UpdatePageRowParams) => {
      const response = await api.functional.request(
        'PUT',
        '/api/v1/projects/version-report/version/page/row',
        params
      )
      return response.json
    }, [])
    const reorderPageRows = useCallback(
      async (params: ReorderPageRowsParams) => {
        const response = await api.functional.request(
          'PUT',
          '/api/v1/projects/version-report/version/page/rows/reorder',
          params
        )
        return response.json
      },
      []
    )
    const applyStructureChanges = useCallback(
      async (params: ApplyStructureChangesParams) => {
        const response = await api.functional.request(
          'POST',
          '/api/v1/projects/version-report/version/pages',
          params
        )
        return response.json
      },
      []
    )
    const evaluateOverview = useCallback(
      async (request: EvaluateOverviewRequest) => {
        const response = await api.functional.request(
          'POST',
          '/api/v1/projects/version-report/evaluate/overview',
          request
        )
        return response.json
      },
      []
    )
    const evaluateProgressByProcess = useCallback(
      async (request: EvaluateProgressReportByProcessRequest) => {
        const response = await api.functional.request(
          'POST',
          '/api/v1/projects/version-report/evaluate/progress-by-process',
          request
        )
        return response.json
      },
      []
    )
    const evaluateProgressByTeam = useCallback(
      async (request: EvaluateProgressReportByTeamRequest) => {
        const response = await api.functional.request(
          'POST',
          '/api/v1/projects/version-report/evaluate/progress-by-team',
          request
        )
        return response.json
      },
      []
    )
    const reCalculateSnapshot = useCallback(async (versionUuid: string) => {
      const response = await api.functional.request(
        'POST',
        '/api/v1/projects/version-report/version/recalculate',
        { versionUuid }
      )
      return response.json
    }, [])
    return {
      fetchVersions,
      fetch,
      createVersion,
      deleteVersion,
      updateVersionName,
      updatePageContent,
      updatePageConfig,
      updatePageRow,
      reorderPageRows,
      applyStructureChanges,
      evaluateOverview,
      evaluateProgressByProcess,
      evaluateProgressByTeam,
      reCalculateSnapshot,
    }
  }
